import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface SelectOption {
  value: string | number
  label: string
}

interface FormSelectProps {
  label: string
  options: SelectOption[]
  value: string | number | null
  onChange: (value: string | number | null) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  isSearchable?: boolean
  isClearable?: boolean
  error?: string
  className?: string
  btnTestId?: string
}

export function FormSelect({
  label: labelText,
  options,
  value,
  onChange,
  placeholder = 'Select...',
  required = false,
  disabled = false,
  isSearchable = true,
  isClearable = true,
  error,
  className,
  btnTestId,
}: FormSelectProps) {
  const safeId = labelText.trim().replace(/\s+/g, '_');
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [isError, setIsError] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const optionsListRef = useRef<HTMLDivElement>(null)

  const selectedOption = useMemo(() => options.find((opt) => opt.value === value), [options, value])

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options
    return options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [options, searchTerm])

  const scrollOptionIntoView = (
    list: HTMLDivElement,
    index: number,
    align: 'start' | 'nearest'
  ) => {
    const el = list.children[index] as HTMLElement | undefined
    if (!el) return
    if (align === 'start') {
      list.scrollTop = el.offsetTop
    } else {
      const elBottom = el.offsetTop + el.offsetHeight
      if (el.offsetTop < list.scrollTop) {
        list.scrollTop = el.offsetTop
      } else if (elBottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = elBottom - list.clientHeight
      }
    }
  }

  useEffect(() => {
    if (!isOpen || !optionsListRef.current) return
    scrollOptionIntoView(optionsListRef.current, highlightedIndex, 'start')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !optionsListRef.current) return
    scrollOptionIntoView(optionsListRef.current, highlightedIndex, 'nearest')
  }, [highlightedIndex, isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const openDropdown = () => {
    const selectedIndex = filteredOptions.findIndex((opt) => opt.value === value)
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0)
    setSearchTerm('')
    setIsOpen(true)
  }

  const toggleOpen = () => {
    if (disabled) return
    if (!isOpen) {
      openDropdown()
      setTimeout(() => inputRef.current?.focus(), 0)
    } else {
      setIsOpen(false)
    }
  }

  const handleSelect = (optionValue: string | number | null) => {
    onChange(optionValue)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setHighlightedIndex(0)
    if (!isOpen) setIsOpen(true)
    if (isError) setIsError(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        setIsOpen(true)
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (filteredOptions.length > 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value)
          setIsError(false)
        } else {
          setIsError(true)
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearchTerm('')
        setIsError(false)
        break
      case 'Tab':
        if (filteredOptions.length > 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value)
          setIsError(false)
        } else {
          setIsOpen(false)
        }
        break
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setSearchTerm('')
  }

  const displayLabel = selectedOption?.label || placeholder

  const triggerClasses = [
    'custom-select__trigger',
    disabled ? 'custom-select-disabled' : '',
    isOpen ? 'custom-select-open' : '',
    isError ? 'custom-select-error' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="form-group">
      <div className="form-floating">
        <div
          className={`custom-select relative w-full${className ? ` ${className}` : ''}`}
          ref={containerRef}
        >
          <div className={triggerClasses} onClick={toggleOpen} data-testid={btnTestId}>
            {!disabled ? (
              <input
                ref={inputRef}
                id={safeId}
                type="text"
                className="w-full border-0 border-none p-0 focus:ring-0 bg-transparent outline-none"
                style={{ color: 'var(--color-on-surface)' }}
                placeholder={displayLabel}
                value={isOpen ? searchTerm : (selectedOption?.label ?? '')}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (!isOpen) openDropdown()
                }}
                onClick={(e) => {
                  if (isOpen) e.stopPropagation()
                }}
                readOnly={!isOpen || !isSearchable}
              />
            ) : (
              <span
                style={{
                  color: !selectedOption
                    ? 'var(--color-on-surface-tertiary)'
                    : 'var(--color-on-surface)',
                }}
                className="block truncate"
              >
                {displayLabel}
              </span>
            )}

            <div className="flex items-center ml-2">
              {selectedOption && !disabled && isClearable && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1 rounded-full mr-1 focus:outline-none"
                  style={{ color: 'var(--color-on-surface-quaternary)' }}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
              <ChevronDownIcon
                style={{ color: 'var(--color-on-surface-tertiary)' }}
                className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>

          {isOpen && (
            <div
              className="absolute z-[100] w-full mt-1 shadow-lg max-h-60 overflow-auto"
              style={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border-secondary)',
              }}
              ref={optionsListRef}
            >
              {filteredOptions.length === 0 ? (
                <div
                  className="px-3 py-2 italic"
                  style={{ color: 'var(--color-on-surface-tertiary)' }}
                >
                  No options found
                </div>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected = value === option.value
                  const isHighlighted = index === highlightedIndex

                  return (
                    <div
                      key={option.value}
                      className={`px-3 py-2 cursor-pointer select-none ${isSelected ? 'font-normal' : ''}`}
                      style={
                        isSelected
                          ? {
                              background: 'var(--gradient-blue-light)',
                              color: 'var(--color-on-surface)',
                            }
                          : isHighlighted
                            ? {
                                background: 'var(--gradient-gray-light)',
                                color: 'var(--color-on-surface)',
                              }
                            : { color: 'var(--color-on-surface-secondary)' }
                      }
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      onClick={() => handleSelect(option.value)}
                    >
                      {option.label}
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>

        <label htmlFor={safeId}>
          {labelText}
          {required && (
            <span style={{ color: 'var(--color-danger)', marginLeft: '0.2rem' }}>*</span>
          )}
        </label>
      </div>

      {error && (
        <span role="alert" className="text-danger field-error">
          {error}
        </span>
      )}
    </div>
  )
}
